import { type JSX } from 'react';
import { type Editor as TiptapEditor } from '@tiptap/react';
import {
  FORMATTING_CONTROLS,
  type FormattingControl,
  type FormattingControlId,
} from './controls.js';
import { useFormattingState, type FormattingSnapshot } from './useFormattingState.js';
import { formatShortcut } from './shortcut-label.js';

interface ToolbarProps {
  editor: TiptapEditor | undefined;
  onInsertField: () => void;
}

// Controls are clustered so related ones stay together when the toolbar wraps
// onto a second row (feature 003 layout). Each group is an inline-flex unit, so
// a group moves to the next row whole rather than splitting mid-cluster.
const GROUPS: readonly { label: string; ids: readonly FormattingControlId[] }[] = [
  { label: 'Text style', ids: ['bold', 'italic', 'underline'] },
  { label: 'Headings', ids: ['h1', 'h2', 'h3'] },
  { label: 'Lists', ids: ['bulletList', 'orderedList'] },
];

const BY_ID: ReadonlyMap<FormattingControlId, FormattingControl> = new Map(
  FORMATTING_CONTROLS.map((control) => [control.id, control]),
);

// The formatting toolbar (feature 003). Every control reflects whether its
// format is in effect at the cursor — visually via the `is-active` class and to
// assistive technology via `aria-pressed` — and disables itself where its
// command cannot run. State comes from `useFormattingState`, derived from the
// live editor (spec FR-001, FR-006, FR-007, FR-014).
export function Toolbar({ editor, onInsertField }: ToolbarProps): JSX.Element {
  const state = useFormattingState(editor);

  return (
    <div className="toolbar" role="toolbar" aria-label="Formatting">
      {GROUPS.map((group) => (
        <div key={group.label} className="toolbar-group" role="group" aria-label={group.label}>
          {group.ids.map((id) => (
            <FormatButton key={id} control={BY_ID.get(id)!} state={state} editor={editor} />
          ))}
        </div>
      ))}
      <div className="toolbar-group toolbar-group--action">
        <button type="button" className="btn-accent-outline" onClick={onInsertField}>
          Insert field
        </button>
      </div>
    </div>
  );
}

interface FormatButtonProps {
  control: FormattingControl;
  state: FormattingSnapshot;
  editor: TiptapEditor | undefined;
}

function FormatButton({ control, state, editor }: FormatButtonProps): JSX.Element {
  const { active, available } = state[control.id];
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={!available}
      className={active ? 'is-active' : undefined}
      title={formatShortcut(control.shortcut)}
      onClick={() => {
        if (editor) control.run(editor);
      }}
    >
      {control.label}
    </button>
  );
}
